import { Component, ReactNode, Suspense, use } from "react"
import { EffectiveTheme, useEffectiveTheme } from "@/hooks/use-effective-theme"
import type { LottieComponentProps } from "lottie-react"
import { useInView } from "react-intersection-observer"

import { ClientRender } from "@/components/client-render"

export type LottieName =
  | "save"
  | "collect"
  | "guide"
  | "trip"
  | "create"
  | "notify"
  | "pin"
  | "bookings"
  | "ideas"
  | "chats"
  | "media"
  | "itinerary"
  | "key-details"
  | "logo-loading"
  | "bookanything-book"
  | "bookanything-loyalty"
  | "bookanything-price-alerts"
  | "bookanything-trip-hub"

interface Props extends Omit<LottieComponentProps, "animationData"> {
  name: LottieName
  eager?: boolean
}

const DARK_LOTTIE_NAMES = new Set<LottieName>(["logo-loading"])

function lottiePath(name: LottieName, theme: EffectiveTheme) {
  let lottieName: string = name
  if (DARK_LOTTIE_NAMES.has(name) && theme === "dark") {
    lottieName = lottieName.concat("-dark")
  }
  return `/lottie/${lottieName}.json`
}

interface LottieResource {
  Lottie: typeof import("lottie-react").default
  data: object
}

// `use()` requires stable promise identity across renders — recreating the
// promise each render would resuspend forever. Cache by path so theme
// switches reuse a fully-loaded entry instantly.
const cache = new Map<string, Promise<LottieResource>>()

function loadLottie(animationPath: string): Promise<LottieResource> {
  const cached = cache.get(animationPath)
  if (cached) return cached

  const promise = Promise.all([
    import("lottie-react"),
    fetch(animationPath).then((res) => {
      if (!res.ok) throw new Error(`Failed to load ${animationPath}`)
      return res.json() as Promise<object>
    }),
  ]).then(([mod, data]) => ({ Lottie: mod.default, data }))
  cache.set(animationPath, promise)
  return promise
}

function LottieRenderer({
  animationPath,
  loop,
  autoplay,
  width,
  height,
  className,
  ...rest
}: Omit<LottieComponentProps, "animationData"> & { animationPath: string }) {
  const { Lottie, data } = use(loadLottie(animationPath))
  return (
    <Lottie
      key={animationPath}
      animationData={data}
      loop={loop}
      autoplay={autoplay}
      style={{ width, height }}
      className={className}
      {...rest}
    />
  )
}

// Local boundary so a fetch / import failure renders nothing instead of
// propagating to Honeybadger — matches the prior console.error + null
// behavior.
class LottieErrorBoundary extends Component<
  { children: ReactNode },
  { hadError: boolean }
> {
  state = { hadError: false }
  static getDerivedStateFromError() {
    return { hadError: true }
  }
  componentDidCatch(error: Error) {
    console.error("Lottie load failed:", error)
  }
  render() {
    return this.state.hadError ? null : this.props.children
  }
}

export function LottieAnimation({
  name,
  eager = false,
  loop = true,
  autoplay = true,
  width = 80,
  height = 80,
  className,
  ...props
}: Props) {
  const theme = useEffectiveTheme()
  const animationPath = lottiePath(name, theme)
  const { ref, inView } = useInView({ triggerOnce: true, skip: eager })

  const placeholder = (
    <div
      ref={eager ? undefined : ref}
      style={{ width, height }}
      className={className}
      aria-label="Loading animation"
    />
  )

  if (!eager && !inView) return placeholder

  // The animation is fetched and drawn in the browser only; the server, and
  // the client through hydration, show the placeholder.
  return (
    <ClientRender loading={placeholder}>
      <LottieErrorBoundary>
        <Suspense fallback={placeholder}>
          <LottieRenderer
            animationPath={animationPath}
            loop={loop}
            autoplay={autoplay}
            width={width}
            height={height}
            className={className}
            {...props}
          />
        </Suspense>
      </LottieErrorBoundary>
    </ClientRender>
  )
}
